import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import '../Customers/Customers.css';
import './Admin.css';

function BackupManagement() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getBackups();
      setBackups(response.backups || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load backups');
      console.error('Error loading backups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setActionLoading('create');
      setError('');
      setSuccess('');
      await adminAPI.createBackup(notes || null);
      setSuccess('Backup created successfully');
      setNotes('');
      loadBackups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create backup');
      console.error('Error creating backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreBackup = async (id) => {
    try {
      setActionLoading(id);
      setError('');
      setSuccess('');
      await adminAPI.restoreBackup(id);
      setSuccess('Backup restored successfully');
      setConfirmRestore(null);
      loadBackups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore backup');
      console.error('Error restoring backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBackup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this backup? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(id);
      setError('');
      setSuccess('');
      await adminAPI.deleteBackup(id);
      setSuccess('Backup deleted successfully');
      loadBackups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete backup');
      console.error('Error deleting backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyBackup = async (id) => {
    try {
      setActionLoading(id);
      setError('');
      const response = await adminAPI.verifyBackup(id);
      if (response.is_valid) {
        setSuccess('Backup verified successfully - file integrity confirmed');
      } else {
        setError('Backup verification failed - file may be corrupted or missing');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify backup');
      console.error('Error verifying backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadBackup = async (id, filename) => {
    try {
      setActionLoading(id);
      setError('');
      const blob = await adminAPI.downloadBackup(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Backup downloaded successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download backup');
      console.error('Error downloading backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 512 * 1024 * 1024) {
        setError('File size exceeds 512MB limit');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUploadBackup = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setActionLoading('upload');
      setError('');
      await adminAPI.uploadBackup(selectedFile, uploadNotes || null);
      setSuccess('Backup uploaded successfully');
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadNotes('');
      loadBackups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload backup');
      console.error('Error uploading backup:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-NZ', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return timestamp;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'failed':
        return 'badge-error';
      case 'restoring':
        return 'badge-warning';
      default:
        return 'badge-default';
    }
  };

  if (loading && backups.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main-wrapper">
          <main className="dashboard-content">
            <div className="customer-list-container">
              <div className="loading">Loading backups...</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Database Backup Management</h1>
              <p className="subtitle">Create, view, and restore database backups</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
                <button onClick={() => setError('')} className="dismiss-btn">x</button>
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
                <button onClick={() => setSuccess('')} className="dismiss-btn">x</button>
              </div>
            )}

            <div className="info-box">
              <h3>Create New Backup</h3>
              <p>Create a backup of the current database state. The backup will include all tables and data.</p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="backup-notes">Notes (optional)</label>
                  <input
                    id="backup-notes"
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this backup..."
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={handleCreateBackup}
                    disabled={actionLoading === 'create'}
                    className="btn btn-primary"
                  >
                    {actionLoading === 'create' ? 'Creating...' : 'Create Backup'}
                  </button>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h3>Upload Backup</h3>
              <p>Upload a previously downloaded backup file. Supported formats: .sql, .sqlite, .db (max 512MB)</p>
              <div className="form-row">
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="btn btn-secondary"
                  >
                    Upload Backup File
                  </button>
                </div>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Created By</th>
                    <th>Restored</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        No backups found. Create your first backup above.
                      </td>
                    </tr>
                  ) : (
                    backups.map((backup) => (
                      <tr key={backup.id}>
                        <td>
                          <div className="backup-name">
                            <strong>{backup.name}</strong>
                            {backup.notes && (
                              <span className="backup-notes">{backup.notes}</span>
                            )}
                          </div>
                        </td>
                        <td>{backup.formatted_size}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(backup.status)}`}>
                            {backup.status}
                          </span>
                        </td>
                        <td>{formatDate(backup.created_at)}</td>
                        <td>{backup.creator_name || 'System'}</td>
                        <td>
                          {backup.restored_at ? (
                            <span>
                              {formatDate(backup.restored_at)}
                              <br />
                              <small>by {backup.restorer_name || 'Unknown'}</small>
                            </span>
                          ) : (
                            <span className="text-muted">Never</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => handleDownloadBackup(backup.id, backup.filename)}
                              disabled={actionLoading === backup.id || !backup.file_exists}
                              className="btn btn-small btn-secondary"
                              title="Download backup file"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => handleVerifyBackup(backup.id)}
                              disabled={actionLoading === backup.id || backup.status === 'restoring'}
                              className="btn btn-small btn-secondary"
                              title="Verify backup integrity"
                            >
                              Verify
                            </button>
                            <button
                              onClick={() => setConfirmRestore(backup.id)}
                              disabled={actionLoading === backup.id || backup.status === 'restoring'}
                              className="btn btn-small btn-warning"
                              title="Restore this backup"
                            >
                              Restore
                            </button>
                            <button
                              onClick={() => handleDeleteBackup(backup.id)}
                              disabled={actionLoading === backup.id || backup.status === 'restoring'}
                              className="btn btn-small btn-danger"
                              title="Delete backup"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {confirmRestore && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h3>Confirm Restore</h3>
                  <p>
                    Are you sure you want to restore backup <strong>#{confirmRestore}</strong>?
                  </p>
                  <p className="warning-text">
                    Warning: This will replace the current database with the data from this backup.
                    This action cannot be undone.
                  </p>
                  <div className="modal-actions">
                    <button
                      onClick={() => setConfirmRestore(null)}
                      className="btn btn-secondary"
                      disabled={actionLoading === confirmRestore}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleRestoreBackup(confirmRestore)}
                      className="btn btn-warning"
                      disabled={actionLoading === confirmRestore}
                    >
                      {actionLoading === confirmRestore ? 'Restoring...' : 'Confirm Restore'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showUploadModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h3>Upload Backup File</h3>
                  <p>Select a backup file to upload. Supported formats: .sql, .sqlite, .db</p>
                  <div className="form-group">
                    <label htmlFor="backup-file">Backup File</label>
                    <input
                      id="backup-file"
                      type="file"
                      accept=".sql,.sqlite,.db"
                      onChange={handleFileSelect}
                      className="form-input"
                    />
                    {selectedFile && (
                      <small className="file-info">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="upload-notes">Notes (optional)</label>
                    <input
                      id="upload-notes"
                      type="text"
                      value={uploadNotes}
                      onChange={(e) => setUploadNotes(e.target.value)}
                      placeholder="Add notes about this backup..."
                      className="form-input"
                    />
                  </div>
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        setShowUploadModal(false);
                        setSelectedFile(null);
                        setUploadNotes('');
                        setError('');
                      }}
                      className="btn btn-secondary"
                      disabled={actionLoading === 'upload'}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUploadBackup}
                      className="btn btn-primary"
                      disabled={actionLoading === 'upload' || !selectedFile}
                    >
                      {actionLoading === 'upload' ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default BackupManagement;